import { createContext, useCallback, useContext, useEffect, useState } from 'react'

type FontLevel = -2 | -1 | 0 | 1 | 2

interface FontSizeContextType {
    fontLevel: FontLevel
    increaseFontSize: () => void
    decreaseFontSize: () => void
    resetFontSize: () => void
    canIncrease: boolean
    canDecrease: boolean
}

const FontSizeContext = createContext<FontSizeContextType>({} as FontSizeContextType)

/**
 * Mapa de nível → escala em porcentagem.
 * Nível 0 = 100% (padrão).
 * Cada step altera 12.5% (padrão WCAG sugerido).
 */
const SCALE_MAP: Record<FontLevel, number> = {
    '-2': 75,
    '-1': 87.5,
    '0': 100,
    '1': 112.5,
    '2': 125,
}

/**
 * FontSizeProvider moderno usando CSS Custom Property `--font-scale`.
 *
 * Em vez de manipular o DOM elemento a elemento com querySelectorAll,
 * este provider altera UMA variável CSS no <html> que pode ser usada
 * via `font-size: calc(1rem * var(--font-scale))` ou simplesmente
 * alterando o font-size base do html.
 *
 * - Persiste no localStorage
 * - 5 níveis: 75%, 87.5%, 100%, 112.5%, 125%
 * - Compatível com unidades `rem` (todo o site escala junto)
 */
export function FontSizeProvider({ children }: { children: React.ReactNode }) {
    const [fontLevel, setFontLevel] = useState<FontLevel>(() => {
        const stored = localStorage.getItem('fontLevel')
        if (stored !== null) {
            const parsed = Number(stored)
            if (parsed >= -2 && parsed <= 2) return parsed as FontLevel
        }
        return 0
    })

    useEffect(() => {
        const root = document.documentElement
        const scale = SCALE_MAP[fontLevel]

        // Alterar o font-size da raiz — tudo que usa `rem` escala automaticamente
        root.style.fontSize = `${scale}%`

        // Também disponibilizar como custom property para uso pontual
        root.style.setProperty('--font-scale', String(scale / 100))

        localStorage.setItem('fontLevel', String(fontLevel))
    }, [fontLevel])

    const increaseFontSize = useCallback(() => {
        setFontLevel(prev => (prev < 2 ? (prev + 1) as FontLevel : prev))
    }, [])

    const decreaseFontSize = useCallback(() => {
        setFontLevel(prev => (prev > -2 ? (prev - 1) as FontLevel : prev))
    }, [])

    const resetFontSize = useCallback(() => {
        setFontLevel(0)
    }, [])

    return (
        <FontSizeContext.Provider
            value={{
                fontLevel,
                increaseFontSize,
                decreaseFontSize,
                resetFontSize,
                canIncrease: fontLevel < 2,
                canDecrease: fontLevel > -2,
            }}
        >
            {children}
        </FontSizeContext.Provider>
    )
}

/**
 * Hook para acessar o controle de tamanho de fonte.
 * @example
 * const { increaseFontSize, decreaseFontSize, resetFontSize, fontLevel } = useFontSize()
 */
export function useFontSize() {
    const context = useContext(FontSizeContext)
    if (!context.increaseFontSize) {
        throw new Error('useFontSize must be used within a FontSizeProvider')
    }
    return context
}
